import {
  getOracle,
  isFeatureProtocolInstance,
  LINKS,
  OracleKey,
  ProtocolKey,
} from "@silo/types";
import { OracleAssets } from "../types";
import { match } from "ts-pattern";
import { DescriptionExternalLink } from "../../description";
import { getAnalyticsMarketLink } from "@silo/utils";
import { ExternalLink } from "../../link";

interface FeedDescriptionProps {
  oracleAssets: OracleAssets;
  oracleKey: OracleKey;
}

export function FeedDescription({
  oracleAssets,
  oracleKey,
}: FeedDescriptionProps): JSX.Element | null {
  const { label } = getOracle(oracleKey);

  return match(oracleAssets)
    .with("base", () => (
      <p>The base asset in this market uses a {label} feed.</p>
    ))
    .with("all", () => <p>All assets in this market use {label} feeds.</p>)
    .with("allBridge", () => (
      <p>The bridge assets in this market use {label} feeds.</p>
    ))
    .with("other", () => null)
    .exhaustive();
}

interface RiskDescriptionProps {
  oracleKey: OracleKey;
  marketName: string;
}

export function RiskDescription({
  oracleKey,
  marketName,
}: RiskDescriptionProps): JSX.Element {
  const { label, riskLevel } = getOracle(oracleKey);

  return match(riskLevel)
    .with("low", () => (
      <p>
        While {label} feeds are broadly trusted, Silo Protocol cannot verify the
        security of {label} feeds.{" "}
        <span className="text-amber-300">Use at your own risk.</span>
      </p>
    ))
    .with("moderate", () => (
      <>
        <p>
          Under extreme market conditions, some {label} oracles may be
          manipulated resulting in bad debt. The Silo Protocol cannot track the
          risk of the oracle or control it.
        </p>
        <p>
          When you use the {marketName} silo, you are assuming unknown risks.{" "}
          <span className="text-amber-300">Use at your own risk.</span>
        </p>
      </>
    ))
    .exhaustive();
}

interface AnalyticsLinkProps {
  protocolKey: ProtocolKey;
  rawMarketSymbol: string;
}

export function AnalyticsLink({
  protocolKey,
  rawMarketSymbol,
}: AnalyticsLinkProps): JSX.Element | null {
  if (!isFeatureProtocolInstance("analytics")(protocolKey)) {
    return null;
  }

  return (
    <DescriptionExternalLink
      className="text-left"
      href={getAnalyticsMarketLink(rawMarketSymbol, protocolKey)}
    >
      View oracle Pump/Dump cost on Silo Analytics
    </DescriptionExternalLink>
  );
}

export function LearnMoreLink(): JSX.Element {
  return (
    <ExternalLink
      variant="action"
      size="small"
      className="w-fit"
      href={LINKS.SILOPEDIA.ORACLE_RISK}
    >
      Learn more at Silopedia
    </ExternalLink>
  );
}

export function LearnMoreAboutChainlinkLink({network, assetName}:{network:string, assetName:string}): JSX.Element {
  return (
    <ExternalLink
      variant="action"
      size="small"
      className="w-fit"
      href={`https://docs.chain.link/data-feeds/price-feeds/addresses/?network=${network}&page=1&search=${assetName}`}
    >
      Learn about Chainlink price feeds
    </ExternalLink>
  );
}

interface PendleOracleLabelProps {
  symbol: string;
  redstoneSymbol: string;
  quoteToken?: string;
}

export function PendleOracleLabel({
  symbol,
  redstoneSymbol,
  quoteToken="ETH"
}: PendleOracleLabelProps) {
  return (
    <>
      <p>
        The value of {symbol} is read from an oracle developed and maintained by
        the Pendle team. To denominate the value of {symbol} in {quoteToken}, we use a
        RedStone {redstoneSymbol} price feed.
      </p>

      <p>
        The Silo Protocol cannot verify the security of this oracle. Use this
        market at your own risk.
      </p>

      <DescriptionExternalLink href={LINKS.PT_ORACLE}>
        Lean more about the Pendle PT oracle
      </DescriptionExternalLink>

      <DescriptionExternalLink href={LINKS.REDSTONE_PRICE_FEEDS}>
        Learn about RedStone price feeds
      </DescriptionExternalLink>
    </>
  );

}

interface SymbioticOracleLabelProps {
  symbol: string;
}

export function SymbioticOracleLabel({
  symbol
}: SymbioticOracleLabelProps) {
  return (
    <>
      <p>
          stETH is a restaking vault on the Symbiotic network with wstETH as the vault’s underlying asset. 
          To calculate {symbol} market value, we hardcode 1{symbol}:1wstETH using a wstETH/stETH conversion rate derived directly from wstETH 
          contract and Chainlink stETH/ETH price feed. The Silo Protocol cannot verify the precision or security of data feeds. 
            {" "}<span className="text-amber-300">Use at your own risk.</span>
      </p>

      <LearnMoreLink />
    </>
  );
}
